import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { GridColDef } from '@mui/x-data-grid';

import { StatusChip } from '@/components/Cards/Chips/StatusChip';
import { getCurrencySymbol } from '@/constants/countries';
import { formatDate } from '@/utils/common';

import type { Invoices, InvoiceStatus } from '@/types/parcel/userData/invoices.interface';

const getStatusColor = (status: InvoiceStatus) => {
  switch (status) {
    case 'success':
      return { bg: '#E8F5E9', color: '#2E7D32' };
    case 'pending':
      return { bg: '#FFF3E0', color: '#E65100' };
    case 'cancelled':
      return { bg: '#FFEBEE', color: '#C62828' };
    default:
      return { bg: '#F5F5F5', color: '#757575' };
  }
};

const getStatusLabel = (status: InvoiceStatus) => {
  switch (status) {
    case 'success':
      return 'Completada';
    case 'pending':
      return 'Pendiente';
    case 'cancelled':
      return 'Cancelada';
    default:
      return status;
  }
};

export const invoicesColumns = (): Array<GridColDef> => [
  {
    field: 'code',
    flex: 1,
    headerName: 'Código',
    width: 150,
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {params.row.code}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'createdAt',
    flex: 1,
    headerName: 'Fecha',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography variant="body2" sx={{ color: (theme) => theme.palette.grey[600] }}>
          {formatDate(params.row.createdAt)}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'period',
    flex: 1.2,
    headerName: 'Período',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: (params) => {
      const period = params.row.period;
      if (!period)
        return (
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
          >
            <Typography variant="body2">-</Typography>
          </Box>
        );
      return (
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
        >
          <Typography variant="body2" sx={{ color: (theme) => theme.palette.grey[600] }}>
            {formatDate(period.startDate)} - {formatDate(period.endDate)}
          </Typography>
        </Box>
      );
    },
  },
  {
    field: 'amount',
    flex: 0.8,
    headerName: 'Monto',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: (params: { row: Invoices }) => {
      const currencySymbol = getCurrencySymbol(params.row.country);
      return (
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {currencySymbol} {params.row.amount.toFixed(2)} {params.row.currency}
          </Typography>
        </Box>
      );
    },
  },
  {
    field: 'status',
    flex: 0.6,
    headerName: 'Estatus',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: (params: { row: Invoices }) => {
      const colors = getStatusColor(params.row.status);
      return (
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
        >
          <StatusChip
            status={getStatusLabel(params.row.status)}
            color={colors.bg}
            bg={colors.color}
          />
        </Box>
      );
    },
  },
  {
    field: 'actions',
    flex: 0.7,
    headerName: 'Descargar',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    resizable: false,
    renderCell: (params: { row: Invoices }) => {
      const isSuccess = params.row.status === 'success';
      const hasFiles = Boolean(params.row.files?.pdf || params.row.files?.xml);

      if (!isSuccess || !hasFiles) {
        return (
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
          >
            <Typography variant="body2">-</Typography>
          </Box>
        );
      }

      return (
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
        >
          <Stack direction="row" spacing={0.5}>
            {params.row.files.pdf && (
              <Tooltip title="Descargar PDF" placement="top">
                <IconButton
                  color="primary"
                  size="small"
                  onClick={() => window.open(params.row.files.pdf, '_blank')}
                >
                  <DescriptionOutlinedIcon />
                </IconButton>
              </Tooltip>
            )}
            {params.row.files.xml && (
              <Tooltip title="Descargar XML" placement="top">
                <IconButton
                  color="secondary"
                  size="small"
                  onClick={() => window.open(params.row.files.xml, '_blank')}
                >
                  <CloudDownloadOutlinedIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>
      );
    },
  },
];
